import { useEffect, useState } from 'react';
import apiClient from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Scraper() {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [vncReady, setVncReady] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, starting, ready, scraping
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionName, setSessionName] = useState('');
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  
  // Feature toggles - load from localStorage or use defaults
  const loadFeaturesFromStorage = () => {
    try {
      const saved = localStorage.getItem('scraperFeatures');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Could not load saved features from localStorage', e);
    }
    // Default values
    return {
      minimalMode: false,
      extractTechnologies: false,
      extractLinkedIn: true,
      extractLocation: true,
      extractIntentData: false,
      extractPhoneSystem: false,
      saveToDatabase: true,
      autoCreateLandingPages: true,
      extractCompanySize: true,
      extractRevenue: false,
      extractIndustry: true
    };
  };

  const [features, setFeatures] = useState(loadFeaturesFromStorage);
  
  // Save features to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('scraperFeatures', JSON.stringify(features));
    } catch (e) {
      console.warn('Could not save features to localStorage', e);
    }
  }, [features]);
  
  const [csvFilename, setCsvFilename] = useState('');
  const [availableCsvFiles, setAvailableCsvFiles] = useState([]);
  const [loadingCsvFiles, setLoadingCsvFiles] = useState(false);

  useEffect(() => {
    checkStatus();
    // Check status every 5 seconds
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Keep session alive while scraper is running (call every 3 minutes)
  useEffect(() => {
    if (!isRunning) return;
    
    const keepAliveInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          const response = await apiClient.post('/auth/keep-alive', {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          // Update token if a new one is provided
          if (response.headers['x-new-access-token']) {
            const newToken = response.headers['x-new-access-token'];
            localStorage.setItem('accessToken', newToken);
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
        }
      } catch (error) {
        console.debug('Keep-alive during scraping failed (non-critical):', error.message);
      }
    }, 3 * 60 * 1000); // Every 3 minutes while scraping
    
    return () => clearInterval(keepAliveInterval);
  }, [isRunning]);

  const checkStatus = async () => {
    try {
      const response = await apiClient.get('/scraper/status');
      setIsRunning(response.data.isRunning);
      if (response.data.session) {
        setSessionInfo(response.data.session);
      }
      if (response.data.isRunning && !vncReady) {
        setVncReady(true);
        setStatus('ready');
      }
    } catch (error) {
      // Silently fail - don't spam console with auth errors
      // The axios interceptor will handle token refresh
      if (error.response?.status !== 401) {
        console.error('Error checking scraper status:', error);
      }
    }
  };

  const loadSessions = async () => {
    try {
      const response = await apiClient.get('/scraper/sessions');
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  useEffect(() => {
    if (showSessions) {
      loadSessions();
    }
  }, [showSessions]);

  const loadCsvFiles = async () => {
    try {
      setLoadingCsvFiles(true);
      const response = await apiClient.get('/scraper/csv-files');
      if (response.data.success) {
        setAvailableCsvFiles(response.data.files || []);
      }
    } catch (error) {
      console.error('Error loading CSV files:', error);
      setAvailableCsvFiles([]);
    } finally {
      setLoadingCsvFiles(false);
    }
  };
  
  useEffect(() => {
    // Load CSV files when component mounts (with a small delay to ensure auth is ready)
    const timer = setTimeout(() => {
      loadCsvFiles();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const toggleFeature = (featureName) => {
    setFeatures(prev => ({
      ...prev,
      [featureName]: !prev[featureName]
    }));
  };

  const handleMinimalModeToggle = (enabled) => {
    setFeatures(prev => {
      const newFeatures = {
        ...prev,
        minimalMode: enabled
      };
      
      // If minimal mode is enabled, disable non-essential features
      if (enabled) {
        newFeatures.extractTechnologies = false;
        newFeatures.extractPhoneSystem = false;
        newFeatures.extractIntentData = false;
        newFeatures.extractLinkedIn = false;
      }
      
      return newFeatures;
    });
  };

  const handleStart = async () => {
    try {
      const response = await apiClient.post('/scraper/start', {
        sessionName: sessionName || undefined,
        csvFilename: csvFilename || undefined,
        features: features
      });
      
      setIsRunning(true);
      setStatus('starting');
      setVncReady(true);
      if (response.data.sessionId) {
        setSessionInfo({
          id: response.data.sessionId,
          session_name: response.data.sessionName,
          status: 'running'
        });
      }
      
      // Wait a moment for browser to open, then show VNC
      setTimeout(() => {
        setStatus('ready');
      }, 3000);
    } catch (error) {
      console.error('Error starting scraper:', error);
      alert('Error starting scraper: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleExportSession = async (sessionId) => {
    try {
      const response = await apiClient.get(`/scraper/sessions/${sessionId}/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scrape_session_${sessionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting session:', error);
      alert('Error exporting session: ' + (error.response?.data?.message || error.message));
    }
  };


  const handleScrape = async () => {
    try {
      await apiClient.post('/scraper/scrape', {});
      setStatus('scraping');
    } catch (error) {
      console.error('Error starting scrape:', error);
      alert('Error starting scrape: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleStop = async () => {
    if (!confirm('Are you sure you want to stop the scraper?')) {
      return;
    }
    
    try {
      await apiClient.post('/scraper/stop', {});
      setIsRunning(false);
      setStatus('idle');
      setVncReady(false);
    } catch (error) {
      console.error('Error stopping scraper:', error);
      alert('Error stopping scraper: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ZoomInfo Scraper</h1>
        <p className="text-gray-600">
          Control the ZoomInfo scraper directly from your browser. Log in, navigate, and scrape contacts.
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Scraper Status</h2>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className="text-sm font-medium">
              {isRunning ? 'Running' : 'Stopped'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {!isRunning ? (
            <div>
              <p className="text-gray-600 mb-4">
                Click "Start Scraper" to launch the browser. You'll be able to log in to ZoomInfo and navigate to the page you want to scrape.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    placeholder="e.g., RingCentral Leads - Jan 2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Append to Existing CSV (Optional)
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={csvFilename}
                      onChange={(e) => setCsvFilename(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      disabled={loadingCsvFiles}
                    >
                      <option value="">-- Create New CSV File --</option>
                      {availableCsvFiles.map((file) => (
                        <option key={file.filename} value={file.filename}>
                          {file.filename} {file.size > 0 ? `(${(file.size / 1024).toFixed(1)} KB)` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={loadCsvFiles}
                      disabled={loadingCsvFiles}
                      className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md disabled:opacity-50"
                      title="Refresh CSV file list"
                    >
                      {loadingCsvFiles ? '...' : '↻'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Select a CSV file to append to, or leave as "Create New CSV File" to start fresh.
                  </p>
                </div>

                {/* Feature Toggles */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="mb-4 pb-3 border-b border-gray-200">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={features.minimalMode}
                        onChange={(e) => handleMinimalModeToggle(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">Minimal Mode (Essential Fields Only)</span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      Only extracts: name, email, phone, job title, company, location, industry, company size
                    </p>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Data Extraction Features</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Technology/Tech Stack */}
                    <label className={`flex items-center space-x-2 cursor-pointer ${features.minimalMode ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={features.extractTechnologies}
                        onChange={() => toggleFeature('extractTechnologies')}
                        disabled={features.minimalMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Extract Technologies</span>
                    </label>

                    {/* LinkedIn */}
                    <label className={`flex items-center space-x-2 cursor-pointer ${features.minimalMode ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={features.extractLinkedIn}
                        onChange={() => toggleFeature('extractLinkedIn')}
                        disabled={features.minimalMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Extract LinkedIn URLs</span>
                    </label>

                    {/* Location */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={features.extractLocation}
                        onChange={() => toggleFeature('extractLocation')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Extract Location</span>
                    </label>

                    {/* Intent Data */}
                    <label className={`flex items-center space-x-2 cursor-pointer ${features.minimalMode ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={features.extractIntentData}
                        onChange={() => toggleFeature('extractIntentData')}
                        disabled={features.minimalMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Extract Intent Data</span>
                    </label>

                    {/* Phone System */}
                    <label className={`flex items-center space-x-2 cursor-pointer ${features.minimalMode ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={features.extractPhoneSystem}
                        onChange={() => toggleFeature('extractPhoneSystem')}
                        disabled={features.minimalMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700">Extract Phone System</span>
                    </label>

                    {/* Company Size */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={features.extractCompanySize}
                        onChange={() => toggleFeature('extractCompanySize')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Extract Company Size</span>
                    </label>

                    {/* Industry */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={features.extractIndustry}
                        onChange={() => toggleFeature('extractIndustry')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Extract Industry</span>
                    </label>

                    {/* Revenue */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={features.extractRevenue}
                        onChange={() => toggleFeature('extractRevenue')}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Extract Revenue</span>
                    </label>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">Processing Options</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={features.saveToDatabase}
                          onChange={() => toggleFeature('saveToDatabase')}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Save to Database</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={features.autoCreateLandingPages}
                          onChange={() => toggleFeature('autoCreateLandingPages')}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Auto-Create Landing Pages</span>
                      </label>

                    </div>
                  </div>
                </div>

                <button
                  onClick={handleStart}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                >
                  Start Scraper
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {status === 'starting' && (
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <p className="text-blue-800">
                    <strong>Starting...</strong> The browser is launching. It will appear in the VNC viewer below in a moment.
                  </p>
                </div>
              )}

              {status === 'ready' && (
                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <p className="text-green-800 mb-3">
                    <strong>Ready!</strong> The browser is now visible below. Please:
                  </p>
                  <ol className="list-decimal list-inside text-green-800 space-y-1">
                    <li>Log in to ZoomInfo in the browser window below</li>
                    <li>Navigate to the page with contacts you want to scrape</li>
                    <li>Click "Start Scraping" button below when ready</li>
                  </ol>
                </div>
              )}

              {status === 'scraping' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <p className="text-yellow-800">
                    <strong>Scraping in progress...</strong> The scraper is extracting contacts from the current page.
                  </p>
                </div>
              )}

              {sessionInfo && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4">
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Session:</strong> {sessionInfo.session_name || 'Unnamed Session'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Status:</strong> {sessionInfo.status} | 
                    <strong className="ml-2">Leads:</strong> {sessionInfo.total_leads || 0} imported
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                {status === 'ready' && (
                  <button
                    onClick={handleScrape}
                    className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                  >
                    Start Scraping
                  </button>
                )}
                <button
                  onClick={handleStop}
                  className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                >
                  Stop Scraper
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VNC Viewer */}
      {vncReady && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-xl font-semibold mb-4">Browser Window (VNC Viewer)</h2>
          <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '600px' }}>
            <iframe
              src={`/vnc/vnc.html?autoconnect=1&resize=scale&password=${encodeURIComponent('leadforge123')}&path=websockify`}
              className="w-full h-full border-0"
              title="VNC Browser Viewer"
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            💡 Tip: You can interact with the browser directly in this viewer. Log in to ZoomInfo and navigate to the page you want to scrape.
          </p>
        </div>
      )}

      {/* Past Scrape Sessions */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Past Scrape Sessions</h2>
          <button
            onClick={() => {
              setShowSessions(!showSessions);
              if (!showSessions) loadSessions();
            }}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {showSessions ? 'Hide' : 'Show'} Sessions
          </button>
        </div>

        {showSessions && (
          <div className="mt-4">
            {sessions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No scrape sessions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessions.map((session) => (
                      <tr key={session.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {session.session_name || 'Unnamed Session'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            session.status === 'completed' ? 'bg-green-100 text-green-800' :
                            session.status === 'running' ? 'bg-blue-100 text-blue-800' :
                            session.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {session.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(session.started_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {session.actual_lead_count || 0} leads
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleExportSession(session.id)}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                            disabled={!session.actual_lead_count || session.actual_lead_count === 0}
                          >
                            Export CSV
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">How to Use</h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800">
          <li>Optionally enter a session name to organize your scrapes</li>
          <li>Click "Start Scraper" to launch the browser</li>
          <li>Wait for the browser window to appear in the VNC viewer</li>
          <li>Log in to ZoomInfo (handle 2FA if needed)</li>
          <li>Navigate to the page with contacts you want to scrape</li>
          <li>Click "Start Scraping" when you're on the right page</li>
          <li>Wait for scraping to complete - contacts will be saved to CSV</li>
          <li>CSV will be automatically imported into LeadForge</li>
          <li>Each scrape session has its own CSV export - view past sessions above</li>
        </ol>
      </div>
    </div>
  );
}

